import { useState } from "react";
import { readPersistedPageSize, writePersistedPageSize } from "./page-size-storage";
import { useEffectAfterMount } from "./useEffectAfterMount";
/**
 * Page-size state seeded from, and persisted to, localStorage.
 *
 * Mirrors usePersistedColumnVisibility: seeded once on mount, layered
 * low → high precedence:
 *
 *   initialPageSize  →  saved preference  →  URL (shareable, in-session)
 *
 * Also returns the effective default (saved preference ?? code default),
 * frozen at mount: the URL-sync layer treats it as the baseline and only
 * writes deviations from it. Re-reading it later would move the baseline the
 * moment the user picks a new size, silently dropping the pageSize URL param.
 *
 * Persisting happens after mount only (same rationale as
 * usePersistedColumnVisibility, see useEffectAfterMount): only an actual
 * page-size change — including one made while on a shared URL — is remembered
 * as the user's preference.
 *
 * Without a `tableId` the persistence calls no-op, so the hook degrades to
 * plain `useState` for anonymous tables.
 */ export function usePersistedPageSize({ tableId, initialPageSize, urlPageSize, enableUrlState }) {
    const [defaultPageSize] = useState(()=>readPersistedPageSize(tableId) ?? initialPageSize);
    const [pageSize, setPageSize] = useState(enableUrlState && urlPageSize ? urlPageSize : defaultPageSize);
    useEffectAfterMount(()=>{
        writePersistedPageSize(tableId, pageSize);
    }, [
        tableId,
        pageSize
    ]);
    return [
        pageSize,
        setPageSize,
        defaultPageSize
    ];
}

//# sourceMappingURL=usePersistedPageSize.js.map